import React, { useState, useEffect, useCallback } from 'react';
import { useLiveQueue } from '../../hooks/useLiveQueue';
import { useNavigate } from 'react-router-dom';
import { workflowAPI } from '../../lib/api';
import { formatDateTime } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { FlaskConical, Check, Clock, TestTube, FileText, Upload } from 'lucide-react';
import { toast } from 'sonner';

const LabQueue = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [completionData, setCompletionData] = useState({
    findings: '',
    notes: '',
    report_file: '',
  });

  const fetchQueue = useCallback(async () => {
    try {
      const response = await workflowAPI.getLabQueue();
      setQueue(response.data);
    } catch (error) {
      toast.error('Failed to load lab queue');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQueue();
    const interval = setInterval(fetchQueue, 30000);
    return () => clearInterval(interval);
  }, [fetchQueue]);

  useLiveQueue('lab', fetchQueue);

  const openCompleteDialog = (item) => {
    setSelectedItem(item);
    setCompletionData({ findings: '', notes: '', report_file: '' });
    setCompleteDialogOpen(true);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setCompletionData({
          ...completionData,
          report_file: reader.result,
          filename: file.name
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleComplete = async () => {
    if (!completionData.findings.trim()) {
      toast.error('Please enter findings/results');
      return;
    }

    try {
      await workflowAPI.completeItem(selectedItem.id, completionData);
      toast.success('Test completed, results sent to doctor & reception');
      setCompleteDialogOpen(false);
      fetchQueue();
    } catch (error) {
      toast.error('Failed to complete test');
    }
  };

  return (
    <div data-testid="lab-queue-page">
      <header className="page-header glass-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-[Manrope]">Laboratory Queue</h1>
            <p className="text-slate-500">Tests waiting to be performed</p>
          </div>
          <Badge className="bg-cyan-500 text-white text-lg px-4 py-2">
            {queue.length} Pending
          </Badge>
        </div>
      </header>

      <div className="page-content">
        <Card>
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="text-lg font-semibold font-[Manrope] flex items-center gap-2">
              <TestTube className="w-5 h-5 text-cyan-500" />
              Pending Tests
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-500">Loading queue...</div>
            ) : queue.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <FlaskConical className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p>No pending tests</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Time</TableHead>
                    <TableHead>Patient</TableHead>
                    <TableHead>Ordered By</TableHead>
                    <TableHead>Test Type</TableHead>
                    <TableHead>Test Name</TableHead>
                    <TableHead>Test ID</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {queue.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm">
                          <Clock className="w-3 h-3 text-slate-400" />
                          {formatDateTime(item.created_at)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{item.patient?.full_name || 'Unknown'}</p>
                          <p className="text-xs text-slate-500">{item.patient?.patient_id}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="text-sm">{item.referred_by_name}</p>
                          <p className="text-xs text-slate-500 capitalize">{item.referred_by_role?.replace('_', ' ')}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={item.reference_type === 'lab_test' ? 'bg-cyan-100 text-cyan-800' : 'bg-blue-100 text-blue-800'}>
                          {item.reference_type === 'lab_test' ? 'Lab Test' : 'Imaging'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <p className="font-medium text-sm">
                          {item.test?.test_name || item.test?.test_type || 'Unknown'}
                        </p>
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {item.test?.test_id}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          className="bg-green-500 hover:bg-green-600"
                          onClick={() => openCompleteDialog(item)}
                        >
                          <Check className="w-4 h-4 mr-2" />
                          Complete Test
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Completion Dialog */}
      <Dialog open={completeDialogOpen} onOpenChange={setCompleteDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">
              Complete Test - {selectedItem?.patient?.full_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm font-semibold text-blue-800 mb-2">Test Details:</p>
              <div className="space-y-1 text-xs text-blue-800">
                <p><strong>Test:</strong> {selectedItem?.test?.test_name}</p>
                <p><strong>Ordered by:</strong> {selectedItem?.referred_by_name} ({selectedItem?.referred_by_role?.replace('_', ' ')})</p>
                <p><strong>Ordered on:</strong> {formatDateTime(selectedItem?.created_at)}</p>
                {selectedItem?.test?.notes && <p><strong>Clinical Notes:</strong> {selectedItem?.test?.notes}</p>}
              </div>
            </div>

            <div>
              <Label>Test Findings/Results *</Label>
              <Textarea
                value={completionData.findings}
                onChange={(e) => setCompletionData({...completionData, findings: e.target.value})}
                placeholder="Enter test results, findings, observations..."
                rows={5}
                required
              />
            </div>

            <div>
              <Label>Additional Notes for Doctor</Label>
              <Textarea
                value={completionData.notes}
                onChange={(e) => setCompletionData({...completionData, notes: e.target.value})}
                placeholder="Any additional notes for the prescribing doctor..."
                rows={3}
              />
            </div>

            <div>
              <Label>Upload Report (PDF/Image) - Optional</Label>
              <div className="flex items-center gap-2">
                <input
                  id="lab-report-upload"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => document.getElementById('lab-report-upload').click()}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {completionData.report_file ? completionData.filename : 'Choose Report File'}
                </Button>
                {completionData.report_file && (
                  <Badge className="bg-green-100 text-green-800">
                    <FileText className="w-3 h-3 mr-1" />
                    Attached
                  </Badge>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-1">Max 10MB. Formats: PDF, JPG, PNG</p>
            </div>

            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-800">
                ✅ Results will be sent to <strong>{selectedItem?.referred_by_name}</strong>
                <br />
                ✅ Patient will be referred to Reception
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setCompleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                className="bg-green-500 hover:bg-green-600"
                onClick={handleComplete}
                disabled={!completionData.findings.trim()}
              >
                <Check className="w-4 h-4 mr-2" />
                Mark Completed & Send Results
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LabQueue;
